import { useEffect, useState } from 'react';
import { Box, Typography, Button, Paper, CircularProgress, Alert } from '@mui/material';
import axios from '../../api/axiosInstance';

const PP = "'Poppins', sans-serif";

export default function AgreementGate() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get('/Agreement/Mine')
      .then(r => {
        if (r.data?.alreadyAccepted) {
          // Defensive — backend says we're already through the gate
          localStorage.setItem('MustAcceptAgreement', 'false');
          window.location.href = '/dashboard';
          return;
        }
        setData(r.data);
      })
      .catch(e => setError(typeof e?.response?.data === 'string' ? e.response.data : 'Failed to load agreement'))
      .finally(() => setLoading(false));
  }, []);

  const onAccept = async () => {
    setSubmitting(true);
    setError('');
    try {
      await axios.post('/Agreement/Accept');
      localStorage.setItem('MustAcceptAgreement', 'false');
      window.location.href = '/dashboard'; // hard reload to refresh user context
    } catch (e) {
      setError(typeof e?.response?.data === 'string' ? e.response.data : 'Failed to accept agreement');
      setSubmitting(false);
    }
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error && !data) return <Box sx={{ p: 4 }}><Alert severity="error">{String(error)}</Alert></Box>;
  if (!data) return null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC', py: { xs: 2, md: 4 } }}>
      <Box sx={{ maxWidth: 920, mx: 'auto', p: { xs: 2, md: 3 } }}>
        <Paper sx={{ p: { xs: 3, md: 5 }, fontFamily: PP }}>
          {/* Logo banner */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, pb: 2, mb: 3, borderBottom: '1px solid #E2E8F0' }}>
            <img src="/webbeyLogo.png" alt="Webbey Sdn Bhd" style={{ height: 64, objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
            <img src="/nexwaveLogo.png" alt="nexwave" style={{ height: 40, objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
          </Box>

          <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5, fontFamily: PP }}>
            Webbey Sdn Bhd · 71-2, Jln Macalister, 10400 George Town, Pulau Pinang
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, fontFamily: PP }}>Sales Agent Agreement</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontFamily: PP }}>
            Effective Date: {data.effectiveDate}
          </Typography>

          <Typography sx={{ mb: 2, fontFamily: PP }}>
            This Sales Agent Agreement is made between <strong>Webbey Sdn Bhd</strong>, located at
            71-2, Jln Macalister, 10400 George Town, Pulau Pinang (the "Company"),
            and <strong>{data.fullName}</strong>, holding IC number <strong>{data.icNumber}</strong>,
            located at <strong>{data.address}</strong> (the "Agent").
            Together, the Company and the Agent are referred to as the "Parties".
          </Typography>

          <Section title="1. Commission & Discounts">
            <ul>
              <li>(Original Price &ndash; Company Discount) &times; Commission Rate &ndash; Agent Discount = Net Commission.</li>
              <li>Commissions paid against Progressive Payment receipts (60% / 100%).</li>
              <li>Paid by the 10th of the month following full Progressive Payment receipt.</li>
              {data.personalComm != null && (
                <li>
                  Your assigned rates &mdash; Personal: <strong>{Number(data.personalComm)}%</strong>
                  {data.downlineOverridingComm != null && <>, Downline Override: <strong>{Number(data.downlineOverridingComm)}%</strong></>}
                  {data.groupName && <> &nbsp;(Group: {data.groupName})</>}
                  .
                </li>
              )}
            </ul>
          </Section>

          <Section title="2. Training & Support">
            <ul>
              <li>Company may offer training, guidance, and resources to help the Agent better understand products, sales process, and operational procedures.</li>
              <li>Agent agrees to attend any required training sessions, briefings, or certification programs.</li>
            </ul>
          </Section>

          <Section title="3. Non-Disclosure Agreement (NDA)">
            <ul>
              <li><strong>Confidential Information</strong> includes, without limitation: pricing structures, discount tables, commission rates, sales strategies, customer and lead lists, supplier and installer arrangements, financial figures, system designs, ROI calculators, training materials, internal processes, software, and any non-public document or data the Agent receives or accesses through the Company.</li>
              <li>Agent shall not disclose, copy, forward, screenshot, photograph, store on personal devices, or use Confidential Information for any purpose other than performing duties under this Agreement.</li>
              <li>Agent shall not share Confidential Information with family members, friends, ex-colleagues, or any third party — including other solar companies, contractors, or competitors — at any time.</li>
              <li>On termination (for any reason), Agent shall immediately return or permanently delete all Confidential Information in their possession, including soft copies, chat exports, and printed materials, and provide written confirmation of doing so on request.</li>
              <li>The confidentiality obligation in this Section survives termination of this Agreement and continues indefinitely for trade secrets, and for at least <strong>2 years</strong> for all other Confidential Information.</li>
              <li>Breach of this NDA entitles the Company to seek injunctive relief, damages, recovery of legal costs, and forfeiture of any commission still owed to the Agent.</li>
            </ul>
          </Section>

          <Section title="4. Exclusivity & Non-Solicitation">
            <ul>
              <li><strong>Exclusivity while engaged.</strong> While this Agreement is active, Agent shall not directly or indirectly join, work for, represent, refer leads to, market on behalf of, hold a stake in, or earn commission from <strong>any other solar company, solar installer, or solar product distributor</strong> in Malaysia or elsewhere — whether as employee, contractor, agent, partner, advisor, or otherwise — without the Company's prior written consent. The Agent's services are committed to the Company on an exclusive basis for the duration of this Agreement.</li>
              <li><strong>Consequence of breach during the Agreement.</strong> If the Company has reasonable grounds to believe the Agent is engaged with another solar company in breach of the exclusivity clause above, the Company may immediately <strong>withhold all unpaid commissions, overrides, incentives, and bonuses</strong> until the matter is resolved. If the breach is confirmed, the withheld amounts are <strong>forfeited in full</strong> and this Agreement is terminated for cause without further compensation.</li>
              <li><strong>Non-solicitation of clients (during and 12 months after).</strong> Agent shall not, directly or indirectly, solicit, contact, or accept business from any client, prospect, lead, or quotation recipient that the Agent dealt with, was introduced to, or learned of through the Company — whether for personal benefit, on behalf of a competitor, or otherwise. This obligation applies during this Agreement and for <strong>12 months</strong> after termination.</li>
              <li><strong>Non-poaching of agents and staff (during and 12 months after).</strong> Agent shall not solicit, recruit, induce, or hire any of the Company's agents, downlines, employees, contractors, suppliers, installers, or business partners to leave the Company or to provide services to a competitor. This obligation applies during this Agreement and for <strong>12 months</strong> after termination.</li>
              <li><strong>Continuing confidentiality.</strong> The NDA in Section 3 — including the prohibition on using Company pricing, leads, designs, ROI calculators, supplier arrangements, and customer data with any other solar business — survives termination and continues to apply for the periods stated in Section 3. Agent acknowledges that any post-termination work in the solar industry must be performed entirely without reference to the Company's Confidential Information.</li>
              <li>Breach of any clause in this Section may trigger immediate termination, legal action, injunctive relief, and recovery of damages and legal costs, in addition to the commission consequences above.</li>
            </ul>
          </Section>

          {(() => {
            // Pull the agent's actual rates so the scenarios match what the
            // agent is signing up for, not a hardcoded 5%/2.5% example.
            const psc = data.personalComm != null ? Number(data.personalComm) : null;
            const cobrokeHalf = psc != null ? (psc / 2) : null;
            const fmt = (v) => v == null ? '—' : (Number.isInteger(v) ? `${v}%` : `${v.toFixed(2).replace(/\.?0+$/, '')}%`);
            // In-house Agent / Referral use a FLAT rate. PSC = FinalPrice × %
            // even on co-broke (no halving). Different scenarios apply.
            const roles = Array.isArray(data.roles) ? data.roles : [];
            const isFlatRate = roles.includes('In-house Agent') || roles.includes('Referral');

            if (isFlatRate) {
              return (
                <Section title="5. Roles & Commission Tiers">
                  <Typography sx={{ fontWeight: 600, mt: 1, fontFamily: PP }}>
                    Flat Rate Agent ({fmt(psc)} on Final Price)
                  </Typography>
                  <ul>
                    <li>You earn <strong>{fmt(psc)} of the Final Price</strong> on every sale you close, regardless of whether it is a solo or co-broking transaction. There is no halving for co-broking.</li>
                    <li>Generate &amp; qualify leads, arrange appointments and site visits.</li>
                    <li>Design system, prepare quotation and necessary documents.</li>
                    <li>Submit paperwork (SEDA/ATAP, financing, insurance).</li>
                    <li>Book installation and coordinate engineering / logistics.</li>
                    <li>Follow up payments through full settlement.</li>
                    <li>Hand over system, register warranties, manage after-sales.</li>
                  </ul>
                  {data.downlineOverridingComm != null && Number(data.downlineOverridingComm) > 0 && (
                    <>
                      <Typography sx={{ fontWeight: 600, mt: 1, fontFamily: PP }}>Downline Override Commission (DOC)</Typography>
                      <ul>
                        <li>When an agent in your downline closes a sale, the Company pays you an additional <strong>{fmt(Number(data.downlineOverridingComm))}</strong> override commission on top of their commission.</li>
                        <li>The DOC is paid by the Company &mdash; not deducted from your downline.</li>
                        <li>DOC is settled on the same Progressive Payment schedule (60% / 100% milestones, paid by the 10th of the following month) as the underlying sale.</li>
                      </ul>
                    </>
                  )}
                </Section>
              );
            }

            // Standard Sales Agent / Leader — solo + co-broke variants.
            return (
          <Section title="5. Roles & Commission Tiers">
            <Typography sx={{ fontWeight: 600, mt: 1, fontFamily: PP }}>Scenario 1 &mdash; Solo Agent ({fmt(psc)})</Typography>
            <ul>
              <li>Generate &amp; qualify leads, arrange appointments and site visits.</li>
              <li>Design system, prepare quotation and necessary documents.</li>
              <li>Submit paperwork (SEDA/NEM, financing, insurance).</li>
              <li>Book installation and coordinate engineering / logistics.</li>
              <li>Follow up payments through full settlement.</li>
              <li>Hand over system, register warranties, manage after-sales.</li>
            </ul>
            <Typography sx={{ fontWeight: 600, mt: 1, fontFamily: PP }}>Scenario 2 &mdash; Co-Broking ({fmt(cobrokeHalf)} each)</Typography>
            <ul>
              <li><strong>Offline Appointment Agent ({fmt(cobrokeHalf)}):</strong> meet customer, present proposal, take site photos / measurements, collect documents (IC, TNB bill, signed quotation), and submit to Online Agent within 48 hours.</li>
              <li><strong>Online Paperwork Agent ({fmt(cobrokeHalf)}):</strong> finalise design, pricing, ROI sheet; submit applications; arrange installation; collect payments; issue handover pack and after-sales support.</li>
            </ul>
            <Typography sx={{ fontWeight: 600, mt: 1, fontFamily: PP }}>Scenario 3 &mdash; Downline Override Commission (DOC)</Typography>
            <ul>
              <li>When an agent in your downline closes a sale, the Company pays you an additional commission as a Downline Override on top of their commission.</li>
              <li><strong>If the downline closes as a Solo Agent (Scenario 1, PSC = {fmt(psc)})</strong>, you earn your <strong>full DOC %</strong> of the final sale price.<br />Example: with DOC = 1%, a RM100,000 sale earns you RM1,000.</li>
              <li><strong>If the downline closes as a Co-Broking agent (Scenario 2, PSC = {fmt(cobrokeHalf)})</strong>, you earn <strong>half of your DOC %</strong> of the final sale price.<br />Example: with DOC = 1%, a RM100,000 co-broking sale earns you RM500.</li>
              <li>The DOC is paid by the Company &mdash; it is not deducted from your downline's own commission.</li>
              <li>DOC is settled on the same Progressive Payment schedule (60% / 100% milestones, paid by the 10th of the following month) as the underlying sale.</li>
              <li>You earn DOC only on direct downline activity assigned to you under this Agreement.</li>
            </ul>
          </Section>
            );
          })()}

          <Section title="6. Agreement Period">
            <ul>
              <li>Effective from acceptance, terminable by either party with 30 days' written notice.</li>
              <li>Company may at its discretion extend up to 6 months after termination for handover, client management, or commission settlement.</li>
              <li>Immediate termination if Agent engages in unauthorized / illegal activity, or breaches confidentiality / non-compete rules.</li>
            </ul>
          </Section>

          <Section title="7. Other Important Terms">
            <ul>
              <li><strong>Independent Contractor.</strong> Agent is not an employee &mdash; responsible for own expenses and taxes.</li>
              <li><strong>Responsibility &amp; Liability.</strong> Agent indemnifies the Company from losses or claims arising from their work.</li>
              <li><strong>Legal Jurisdiction.</strong> Governed by the laws of Malaysia; disputes handled in Selangor.</li>
              <li><strong>Reporting.</strong> Regular updates (e.g. weekly reports or monthly check-ins) as requested.</li>
              <li><strong>Data Protection.</strong> Comply with applicable privacy laws when handling customer or personal data.</li>
            </ul>
          </Section>

          <Box sx={{ mt: 4, p: 2.5, bgcolor: '#F8FAFC', borderRadius: 1.5, border: '1px solid #E2E8F0' }}>
            <Typography sx={{ fontSize: 14, fontFamily: PP }}>
              <strong>Agent:</strong> {data.fullName} &nbsp;&middot;&nbsp;
              <strong>IC:</strong> {data.icNumber} &nbsp;&middot;&nbsp;
              <strong>Date:</strong> {data.effectiveDate}
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.75, fontFamily: PP }}>
              By clicking the button below, you confirm that you have read, understood, and agreed to all the terms above.
            </Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button variant="contained" size="large" disabled={submitting} onClick={onAccept} sx={{
              fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px',
              background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', boxShadow: 'none', px: 3, py: 1.2,
              '&:hover': { background: 'linear-gradient(135deg, #0F62CC, #0A4FA3)', boxShadow: 'none' },
            }}>
              {submitting ? 'Submitting…' : 'I have read and agree to the terms above'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}

function Section({ title, children }) {
  return (
    <Box sx={{ mt: 3 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 0.5, fontFamily: PP }}>{title}</Typography>
      <Box sx={{ '& ul': { my: 0.5, pl: 3 }, '& li': { mb: 0.5, fontFamily: PP, fontSize: 13.5 } }}>
        {children}
      </Box>
    </Box>
  );
}
